<template>
  <div class="qd-root" :class="'qd-theme-' + theme">

    <!-- Header -->
    <header class="qd-header">
      <div class="d-flex align-items-center gap-3">
        <img v-if="cfg.show_logo == 1" :src="$brand.logo" alt="HMS" class="qd-logo" />
        <div>
          <div class="qd-hospital">{{ cfg.hospital_name || 'Hospital Management System' }}</div>
          <div class="qd-branch">{{ cfg.branch_name || '' }}</div>
        </div>
      </div>
      <div v-if="cfg.show_clock == 1" class="qd-clock">
        <div class="qd-time">{{ clockTime }}</div>
        <div class="qd-date">{{ clockDate }}</div>
      </div>
    </header>

    <!-- Body -->
    <main class="qd-body">

      <!-- ERROR STATE -->
      <div v-if="error" class="qd-error">
        <i class="mdi mdi-alert-circle"></i>
        <div>{{ error }}</div>
      </div>

      <!-- INITIAL LOADING -->
      <div v-else-if="initialLoading" class="qd-loading">
        <div class="spinner-border"></div>
        <div>Loading queue display...</div>
      </div>

      <template v-else>
        <!-- NOW SERVING -->
        <section class="qd-now">
          <h2 class="qd-section-title"><i class="mdi mdi-volume-high"></i> Now Serving</h2>
          <div class="qd-now-grid" v-if="nowServing.length">
            <div v-for="t in nowServing" :key="t.ticket_id" class="qd-now-card" :class="{ 'qd-now-pulse': t.ticket_id === pulseTicketId }" :style="cardStyle(t.color)">
              <div class="qd-now-num">{{ t.ticket_number }}</div>
              <div class="qd-now-info">
                <div class="qd-now-dept">{{ t.dept_name }}</div>
                <div class="qd-now-counter" v-if="t.counter_name"><i class="mdi mdi-arrow-right me-1"></i>{{ t.counter_name }}</div>
              </div>
            </div>
          </div>
          <div v-else class="qd-now-empty">No tickets currently being called</div>
        </section>

        <!-- NEXT UP -->
        <section class="qd-next">
          <h2 class="qd-section-title"><i class="mdi mdi-clock-outline"></i> Next Up</h2>
          <div class="qd-next-grid">
            <div v-for="(t, ti) in nextUp" :key="t.ticket_id" class="qd-next-row" :style="rowStyle(t.color)">
              <span class="qd-next-num">{{ t.ticket_number }}</span>
              <span class="qd-next-dept">{{ t.dept_name }}</span>
              <span v-if="t.priority !== 'normal'" class="qd-next-prio" :class="'qd-prio-' + t.priority">{{ formatPriority(t.priority) }}</span>
              <span v-if="ti === 0" class="qd-next-flag"><i class="mdi mdi-arrow-down-bold-circle"></i> Up next</span>
            </div>
            <div v-if="!nextUp.length" class="qd-next-empty">No tickets in waiting</div>
          </div>
        </section>

        <!-- STATS BAR -->
        <section class="qd-stats" v-if="stats">
          <div class="qd-stat">
            <div class="qd-stat-num">{{ stats.total_today || 0 }}</div>
            <div class="qd-stat-lbl">Total Today</div>
          </div>
          <div class="qd-stat">
            <div class="qd-stat-num qd-text-green">{{ stats.served || 0 }}</div>
            <div class="qd-stat-lbl">Served</div>
          </div>
          <div class="qd-stat">
            <div class="qd-stat-num qd-text-amber">{{ stats.waiting || 0 }}</div>
            <div class="qd-stat-lbl">Waiting</div>
          </div>
          <div class="qd-stat">
            <div class="qd-stat-num">{{ stats.avg_wait ? Math.round(stats.avg_wait) + 'm' : '—' }}</div>
            <div class="qd-stat-lbl">Avg Wait</div>
          </div>
        </section>
      </template>

    </main>

    <!-- Marquee scroll -->
    <footer class="qd-footer" v-if="cfg.scroll_message">
      <div class="qd-marquee"><span>{{ cfg.scroll_message }} &nbsp; · &nbsp; {{ cfg.scroll_message }} &nbsp; · &nbsp; {{ cfg.scroll_message }}</span></div>
    </footer>

    <!-- Audio toggle (browsers block autoplay; user must click once) -->
    <button v-if="!audioEnabled" class="qd-audio-prompt" @click="enableAudio">
      <i class="mdi mdi-volume-off me-1"></i>Tap to enable announcements
    </button>
    <button v-else class="qd-audio-toggle" @click="audioEnabled = false" title="Mute">
      <i class="mdi mdi-volume-high"></i>
    </button>
  </div>
</template>

<style>
/* Public display — own dark/light themes, NOT bound to user theme */
.qd-theme-light { --qd-bg: #f4f6f9; --qd-card: #ffffff; --qd-h: #1a1a1a; --qd-s: #777; --qd-br: #dee2e8; --qd-primary: #0047AB; }
.qd-theme-dark  { --qd-bg: #0a1628; --qd-card: #0f2347; --qd-h: #E8EDF4; --qd-s: #8BA4C4; --qd-br: #1a3a6a; --qd-primary: #4FC3F7; }
</style>

<style scoped>
* { box-sizing: border-box; }

.qd-root {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--qd-bg);
  color: var(--qd-h);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow: hidden;
}

/* Header */
.qd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  border-bottom: 1px solid var(--qd-br);
  background: var(--qd-card);
}
.qd-logo { height: 56px; }
.qd-hospital { font-weight: 800; font-size: 28px; letter-spacing: -0.5px; }
.qd-branch { color: var(--qd-s); font-size: 16px; }
.qd-clock { text-align: right; }
.qd-time { font-size: 36px; font-weight: 800; letter-spacing: -1px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.qd-date { color: var(--qd-s); font-size: 14px; }

/* Body */
.qd-body { flex: 1; overflow: hidden; padding: 24px 32px; display: flex; flex-direction: column; gap: 24px; }

.qd-loading, .qd-error { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: var(--qd-s); }
.qd-loading .spinner-border { color: var(--qd-primary); }
.qd-error i { font-size: 64px; color: #dc3545; }

.qd-section-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--qd-s);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Now Serving */
.qd-now { flex: 0 0 auto; }
.qd-now-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 16px;
}
.qd-now-card {
  background: var(--qd-card);
  border: 2px solid var(--qd-primary);
  border-radius: 16px;
  padding: 24px 28px;
  display: flex;
  align-items: center;
  gap: 24px;
  box-shadow: 0 8px 30px rgba(0,0,0,.10);
  position: relative;
  overflow: hidden;
}
.qd-now-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, transparent 60%, currentColor 200%);
  opacity: 0.05;
  pointer-events: none;
}
.qd-now-num {
  font-size: 64px;
  font-weight: 900;
  letter-spacing: -2px;
  color: var(--qd-primary);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  line-height: 1;
}
.qd-now-info { flex: 1; min-width: 0; }
.qd-now-dept { font-size: 24px; font-weight: 700; }
.qd-now-counter { font-size: 18px; color: var(--qd-s); margin-top: 4px; display: flex; align-items: center; }

.qd-now-pulse {
  animation: qd-pulse 1.5s ease-in-out 2;
}
@keyframes qd-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 8px 30px rgba(0,0,0,.10); }
  50%      { transform: scale(1.03); box-shadow: 0 12px 50px var(--qd-primary); }
}

.qd-now-empty { padding: 40px; text-align: center; color: var(--qd-s); font-size: 18px; background: var(--qd-card); border: 1px solid var(--qd-br); border-radius: 12px; }

/* Next Up */
.qd-next { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.qd-next-grid { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; }
.qd-next-row {
  background: var(--qd-card);
  border: 1px solid var(--qd-br);
  border-radius: 10px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 18px;
  font-size: 18px;
}
.qd-next-num { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 800; font-size: 22px; min-width: 110px; color: var(--qd-primary); }
.qd-next-dept { flex: 1; font-weight: 600; }
.qd-next-prio { padding: 3px 10px; border-radius: 50px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
.qd-prio-emergency        { background: rgba(220,53,69,.15); color: #dc3545; }
.qd-prio-elderly_disabled { background: rgba(155,89,182,.15); color: #9b59b6; }
.qd-prio-urgent           { background: rgba(245,158,11,.15); color: #f59e0b; }
.qd-next-flag { color: var(--qd-primary); font-size: 13px; font-weight: 700; }
.qd-next-empty { padding: 40px; text-align: center; color: var(--qd-s); }

/* Stats */
.qd-stats { display: flex; gap: 16px; flex: 0 0 auto; }
.qd-stat { flex: 1; background: var(--qd-card); border: 1px solid var(--qd-br); border-radius: 12px; padding: 14px 20px; }
.qd-stat-num { font-size: 32px; font-weight: 800; color: var(--qd-primary); line-height: 1.1; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.qd-stat-lbl { color: var(--qd-s); font-size: 13px; text-transform: uppercase; letter-spacing: 1px; margin-top: 4px; }
.qd-text-green { color: #28a745; }
.qd-text-amber { color: #f59e0b; }

/* Footer marquee */
.qd-footer {
  background: var(--qd-card);
  border-top: 1px solid var(--qd-br);
  padding: 14px 0;
  overflow: hidden;
  flex: 0 0 auto;
}
.qd-marquee {
  white-space: nowrap;
  animation: qd-scroll 40s linear infinite;
}
.qd-marquee span {
  font-size: 16px;
  font-weight: 600;
  color: var(--qd-h);
}
@keyframes qd-scroll {
  0%   { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
}

/* Audio prompt */
.qd-audio-prompt, .qd-audio-toggle {
  position: fixed;
  bottom: 70px;
  right: 24px;
  background: var(--qd-primary);
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 10px 18px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0,0,0,.2);
  z-index: 50;
}
.qd-audio-toggle { padding: 10px; border-radius: 50%; width: 44px; height: 44px; }

@media (max-width: 768px) {
  .qd-body { padding: 16px; }
  .qd-now-num { font-size: 48px; }
  .qd-now-card { padding: 16px; gap: 16px; }
  .qd-hospital { font-size: 20px; }
  .qd-time { font-size: 24px; }
}
</style>

<script>
/* eslint-disable */
import { execute2, hms } from "@/api";

export default {
  name: "QueueDisplay",
  data() {
    return {
      token: "",
      initialLoading: true,
      error: "",
      cfg: {
        hospital_name: "",
        branch_name: "",
        show_logo: 1,
        show_clock: 1,
        scroll_message: "",
        announcement_voice: "en-US",
        announcement_volume: 1.0
      },
      theme: "light",
      nowServing: [],
      nextUp: [],
      stats: null,
      announcedTickets: new Set(),
      pulseTicketId: null,
      clockTime: "",
      clockDate: "",
      pollTimer: null,
      clockTimer: null,
      audioEnabled: false,
      ttsVoice: null
    };
  },
  mounted() {
    this.token = this.$route.query.token || "";
    if (!this.token) {
      this.error = "Display token missing. Append ?token=YOUR_TOKEN to the URL.";
      this.initialLoading = false;
      return;
    }
    this.startClock();
    this.loadConfig();
    this.poll();
    this.pollTimer = setInterval(() => this.poll(), 3000);

    // Try to load voices (deferred — browsers populate them async)
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => this.pickVoice();
      this.pickVoice();
    }
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.clockTimer) clearInterval(this.clockTimer);
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  },
  methods: {
    formatPriority(p) {
      var m = { emergency: 'Emergency', elderly_disabled: 'Elderly/Disabled', urgent: 'Urgent', normal: 'Normal' };
      return m[p] || p;
    },
    cardStyle(color) {
      if (!color) return "";
      return "border-color:" + color + "; color:" + color + ";";
    },
    rowStyle(color) {
      if (!color) return "";
      return "border-left: 4px solid " + color + ";";
    },

    startClock() {
      this.tickClock();
      this.clockTimer = setInterval(() => this.tickClock(), 1000);
    },
    tickClock() {
      var now = new Date();
      var hh = String(now.getHours()).padStart(2, '0');
      var mm = String(now.getMinutes()).padStart(2, '0');
      var ss = String(now.getSeconds()).padStart(2, '0');
      this.clockTime = hh + ':' + mm + ':' + ss;
      this.clockDate = now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    },

    pickVoice() {
      if (!('speechSynthesis' in window)) return;
      var voices = window.speechSynthesis.getVoices();
      if (!voices.length) return;
      var langPref = (this.cfg.announcement_voice || 'en-US').toLowerCase();
      var langRoot = langPref.split('-')[0];

      // Curated female-voice name hints across platforms (macOS, iOS, Windows,
      // Android, Chrome). Order = preference; higher = better/more natural.
      var femaleHints = [
        // Apple neural (macOS / iOS) — very natural
        'ava', 'samantha', 'victoria', 'allison', 'susan', 'karen', 'moira', 'tessa', 'serena', 'kate', 'fiona', 'zoe',
        // Google (Chrome / Android)
        'google uk english female', 'google us english', 'en-gb-standard-a', 'en-gb-wavenet-a',
        // Microsoft (Edge / Windows) — Aria and Jenny are neural
        'aria', 'jenny', 'zira', 'hazel', 'catherine',
        // Generic markers
        'female', 'woman'
      ];
      var qualityHints = ['neural', 'premium', 'enhanced', 'natural', 'wavenet'];

      function score(v) {
        var s = 0;
        var name = (v.name || '').toLowerCase();
        var vlang = (v.lang || '').toLowerCase();
        if (vlang === langPref) s += 100;
        else if (vlang.startsWith(langRoot)) s += 60;
        // Female name match (earlier in list = higher weight)
        for (var i = 0; i < femaleHints.length; i++) {
          if (name.indexOf(femaleHints[i]) !== -1) { s += (60 - i); break; }
        }
        // Quality hints
        qualityHints.forEach(function (q) { if (name.indexOf(q) !== -1) s += 20; });
        // Penalise clearly synthetic engines
        if (name.indexOf('compact') !== -1) s -= 30;
        if (name.indexOf('eloquence') !== -1) s -= 30;
        // Prefer non-default (defaults are usually the flat robot)
        if (v.default) s -= 5;
        return s;
      }

      var ranked = voices.slice().sort(function (a, b) { return score(b) - score(a); });
      this.ttsVoice = ranked[0] || null;
    },

    async loadConfig() {
      try {
        var r = await execute2({ function: "getDisplayConfig", token: this.token }, hms);
        if (r.data && r.data.success && r.data.data && r.data.data.Config) {
          var c = r.data.data.Config;
          this.cfg = {
            hospital_name:        c.hospital_name || "",
            branch_name:          c.branch_name || "",
            show_logo:            c.show_logo == null ? 1 : Number(c.show_logo),
            show_clock:           c.show_clock == null ? 1 : Number(c.show_clock),
            scroll_message:       c.scroll_message || "",
            announcement_voice:   c.announcement_voice || "en-US",
            announcement_volume:  c.announcement_volume == null ? 1.0 : Number(c.announcement_volume)
          };
          this.theme = c.theme === 'dark' ? 'dark' : 'light';
          this.pickVoice();
        } else {
          this.error = (r.data && r.data.message) || "Invalid display token.";
        }
      } catch (e) {
        this.error = "Failed to load display config.";
      }
    },

    async poll() {
      try {
        var r = await execute2({ function: "getDisplayState", token: this.token }, hms);
        if (r.data && r.data.success && r.data.data && r.data.data.State) {
          var s = r.data.data.State;
          var newNow = s.now_serving || [];

          // Detect newly-called tickets to announce
          newNow.forEach(t => {
            if (!this.announcedTickets.has(t.ticket_id)) {
              this.announcedTickets.add(t.ticket_id);
              this.announceTicket(t);
              this.pulseTicketId = t.ticket_id;
              setTimeout(() => { if (this.pulseTicketId === t.ticket_id) this.pulseTicketId = null; }, 3500);
            }
          });

          this.nowServing = newNow;
          this.nextUp = s.next_up || [];
          this.stats = s.stats || null;
          this.error = "";
          this.initialLoading = false;
        } else if (r.data && !r.data.success && this.initialLoading) {
          this.error = r.data.message || "Display unavailable.";
          this.initialLoading = false;
        }
      } catch (e) {
        if (this.initialLoading) {
          this.error = "Connection error. Retrying...";
          this.initialLoading = false;
        }
      }
    },

    enableAudio() {
      // User gesture required to unlock speech in most browsers
      if ('speechSynthesis' in window) {
        var u = new SpeechSynthesisUtterance(' ');
        u.volume = 0;
        window.speechSynthesis.speak(u);
        this.audioEnabled = true;
      }
    },

    announceTicket(t) {
      if (!this.audioEnabled) return;
      if (!('speechSynthesis' in window)) return;

      // Split the ticket number into a phonetic-friendly form.
      // Letters stay grouped (e.g. "OPD" → "O P D"), digits are grouped as one number
      // so "0005" reads as "zero zero zero five" naturally instead of "zero, zero…" flat.
      var raw = (t.ticket_number || '').replace('-', '');
      var letters = raw.match(/[A-Za-z]+/) ? raw.match(/[A-Za-z]+/)[0].toUpperCase() : '';
      var digits  = raw.match(/[0-9]+/)   ? raw.match(/[0-9]+/)[0]   : '';
      var lettersSpaced = letters.split('').join(' ');
      // Group digits with light commas so the TTS pauses naturally between them
      var digitsSpaced  = digits.split('').join(' ');

      var counter = t.counter_name ? ', counter ' + t.counter_name : '';
      // Commas and full stops = natural pauses in TTS; keep phrasing warm and short.
      var msg = 'Ticket ' + lettersSpaced + ', ' + digitsSpaced +
                '. Please proceed to ' + (t.dept_name || 'reception') + counter + '.';

      // Repeat once for clarity in a noisy waiting hall
      var repeat = ' Once more: ' + lettersSpaced + ', ' + digitsSpaced + '.';

      var utt = new SpeechSynthesisUtterance(msg + repeat);
      if (this.ttsVoice) utt.voice = this.ttsVoice;
      utt.lang   = (this.ttsVoice && this.ttsVoice.lang) || this.cfg.announcement_voice || 'en-US';
      utt.rate   = 0.95;   // slightly slower than default = more natural
      utt.pitch  = 1.05;   // hint towards a warmer, slightly higher (feminine) delivery
      utt.volume = Math.max(0, Math.min(1, parseFloat(this.cfg.announcement_volume) || 1));

      // Some engines cache prior utterances; cancel avoids overlapping "still speaking".
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utt);
    }
  }
};
</script>
